import { useEffect, useState, useRef } from "react";
import { lumiFetch, fmtDateTime, fmtDateTimeShort } from "./lumiApi";
import { toast } from "./lumiToast";

const STATUS_TABS = [
  { id: "new", label: "New", color: "amber-300" },
  { id: "handled", label: "Handled", color: "emerald-300" },
  { id: "archived", label: "Archived", color: "white/40" },
  { id: "all", label: "All", color: "white/60" },
];

const INTENT_LABEL = {
  question: "💭 Question",
  feedback: "💬 Feedback",
  purchase_inquiry: "🛒 Purchase",
  technical_issue: "🛠️ Technical",
  partnership: "🤝 Partnership",
  unsubscribe_request: "🚪 Unsubscribe",
  thank_you: "💛 Thank you",
  other: "📩 Other",
};

export default function LumiInbox() {
  const [status, setStatus] = useState("new");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ new: 0, handled: 0, archived: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        lumiFetch(`/inbox?status=${s}`),
        lumiFetch(`/inbox/stats/summary`),
      ]);
      setItems(list);
      setStats(st);
    } catch (e) {
      if (e?.data?.detail?.includes("not configured")) {
        toast.error("Set your mailbox password in Settings first");
      } else {
        toast.error(e?.data?.detail || "Could not load inbox");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(status); }, [status]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await lumiFetch("/inbox/sync", { method: "POST" });
      toast.success(`Synced ${r.fetched} messages, ${r.new} new ✨`);
      load(status);
    } catch (e) { toast.error(e?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const open = async (it) => {
    try {
      const full = await lumiFetch(`/inbox/${it.id}`);
      setSelected(full);
    } catch (e) { toast.error("Could not open message"); }
  };

  const closeDetail = (refresh = false) => {
    setSelected(null);
    if (refresh) load(status);
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="lumi-inbox">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold">Inbox</h2>
          <span className="text-xs text-white/40">{stats.new} new · {stats.handled} handled · {stats.total} total</span>
        </div>
        <div className="flex items-center gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatus(t.id)}
              data-testid={`lumi-inbox-tab-${t.id}`}
              className={`text-xs px-3 py-1 rounded-full transition-all ${status === t.id ? "bg-white text-black" : "text-white/60 hover:text-white border border-white/10"}`}
            >
              {t.label}{t.id !== "all" ? ` (${stats[t.id] || 0})` : ""}
            </button>
          ))}
          <button data-testid="lumi-inbox-sync" onClick={sync} disabled={syncing} className="text-xs px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 ml-2">
            {syncing ? "Syncing…" : "↻ Sync"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full">
        {loading ? (
          <div className="text-center text-white/40 text-sm py-10">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-10 text-center text-white/40 text-sm">
            {status === "new"
              ? "No new replies. Click ↻ Sync to check the mailbox."
              : `No ${status} messages yet.`}
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/10">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => open(it)}
                data-testid={`lumi-inbox-row-${it.id}`}
                className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-start gap-3"
              >
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${it.status === "new" ? "bg-amber-300" : it.status === "handled" ? "bg-emerald-300" : "bg-white/20"}`}></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium truncate">{it.from_name || it.from_email}</span>
                    <span className="text-xs text-white/40 shrink-0">{fmtDateTime(it.received_at)}</span>
                  </div>
                  <div className="text-sm text-white/80 truncate">{it.subject}</div>
                  {it.summary && (
                    <div className="text-xs text-white/50 mt-1 line-clamp-2">
                      {INTENT_LABEL[it.intent] || ""} · {it.summary}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && <InboxDetail message={selected} onClose={closeDetail} onUpdate={(m) => setSelected(m)} />}
    </div>
  );
}

function InboxDetail({ message, onClose, onUpdate }) {
  const [reply, setReply] = useState(message.ai_reply_draft || "");
  const [toEmail, setToEmail] = useState(message.from_email || "");
  const [analysing, setAnalysing] = useState(false);
  const [sending, setSending] = useState(false);

  // Chat-refine panel state
  const [refineMessages, setRefineMessages] = useState([]); // {role:"you"|"lumi", content}
  const [refineInput, setRefineInput] = useState("");
  const [refining, setRefining] = useState(false);
  const refineEndRef = useRef(null);

  // Snippets state
  const [snippets, setSnippets] = useState([]);
  const [showSnippets, setShowSnippets] = useState(false);
  const [snippetTab, setSnippetTab] = useState("text"); // "text" | "video"
  const [snippetQuery, setSnippetQuery] = useState("");
  const replyRef = useRef(null);

  // Conversation history (thread)
  const [thread, setThread] = useState(null); // {turns: [...]} | null
  const [showHistory, setShowHistory] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);

  useEffect(() => { setReply(message.ai_reply_draft || ""); }, [message.ai_reply_draft]);
  useEffect(() => { setToEmail(message.from_email || ""); }, [message.from_email]);
  useEffect(() => {
    lumiFetch("/snippets").then(setSnippets).catch(() => {});
  }, []);
  useEffect(() => {
    refineEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [refineMessages, refining]);
  useEffect(() => {
    setLoadingThread(true);
    lumiFetch(`/inbox/${message.id}/thread`)
      .then(setThread)
      .catch(() => setThread(null))
      .finally(() => setLoadingThread(false));
  }, [message.id]);

  // Detect forwarded messages — reply would go to the forwarder, not the original sender.
  const subject = message.subject || "";
  const body = message.body_text || "";
  const isForwarded =
    /^\s*(fwd|fw):/i.test(subject) ||
    /-+\s*Forwarded message\s*-+/i.test(body) ||
    /Begin forwarded message/i.test(body);

  const insertSnippet = (snippet) => {
    const ta = replyRef.current;
    if (!ta) {
      setReply((prev) => (prev ? `${prev}\n\n${snippet.body}` : snippet.body));
    } else {
      const start = ta.selectionStart ?? reply.length;
      const end = ta.selectionEnd ?? reply.length;
      const next = reply.slice(0, start) + snippet.body + reply.slice(end);
      setReply(next);
      // restore cursor after the inserted text
      setTimeout(() => {
        ta.focus();
        const pos = start + snippet.body.length;
        ta.setSelectionRange(pos, pos);
      }, 0);
    }
    setShowSnippets(false);
    setSnippetQuery("");
    toast.success(`Inserted "${snippet.label}"`);
  };

  const refine = async () => {
    const instr = refineInput.trim();
    if (!instr) return;
    setRefineMessages((m) => [...m, { role: "you", content: instr }]);
    setRefineInput("");
    setRefining(true);
    try {
      const r = await lumiFetch(`/inbox/${message.id}/refine`, {
        method: "POST",
        body: JSON.stringify({ instruction: instr, current_draft: reply }),
      });
      setReply(r.new_draft || reply);
      const fetched = (r.fetched_urls || []).filter((u) => u.ok).map((u) => u.url);
      const failed = (r.fetched_urls || []).filter((u) => !u.ok);
      const lumiText = [
        fetched.length ? `🌐 Read ${fetched.length} page${fetched.length > 1 ? "s" : ""}: ${fetched.join(", ")}` : "",
        failed.length ? `⚠️ Couldn't read ${failed.length}: ${failed.map((f) => `${f.url} (${f.error})`).join("; ")}` : "",
        r.message || "Done ✨",
      ].filter(Boolean).join("\n");
      setRefineMessages((m) => [...m, { role: "lumi", content: lumiText }]);
    } catch (e) {
      const detail = e?.data?.detail || e?.message || "I couldn't refine that — please try again.";
      setRefineMessages((m) => [...m, { role: "lumi", content: `Sorry — ${detail}` }]);
    } finally {
      setRefining(false);
    }
  };

  const analyse = async () => {
    setAnalysing(true);
    try {
      const r = await lumiFetch(`/inbox/${message.id}/analyse`, { method: "POST" });
      onUpdate({ ...message, summary: r.summary, intent: r.intent, tone: r.tone, ai_reply_draft: r.draft_reply });
      toast.success("Lumi analysed it ✨");
    } catch (e) { toast.error(e?.data?.detail || "Analyse failed"); }
    finally { setAnalysing(false); }
  };

  const send = async () => {
    if (!reply.trim()) return toast.error("Reply is empty");
    const target = (toEmail || "").trim();
    if (!target) return toast.error("Reply-to address is empty");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(target)) return toast.error("That doesn't look like a valid email address");
    if (!window.confirm(`Send this reply to ${target}?`)) return;
    setSending(true);
    try {
      const r = await lumiFetch(`/inbox/${message.id}/send-reply`, {
        method: "POST",
        body: JSON.stringify({ body: reply, to_email: target }),
      });
      toast.success(`Reply sent to ${r.sent_to} 💌`);
      onClose(true);
    } catch (e) { toast.error(e?.data?.detail || e?.message || "Send failed"); }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    try {
      await lumiFetch(`/inbox/${message.id}/status`, { method: "POST", body: JSON.stringify({ status }) });
      toast.success(status === "new" ? "Re-opened" : `Marked ${status}`);
      onClose(true);
    } catch (e) { toast.error(e?.data?.detail || "Could not update"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" data-testid="lumi-inbox-detail">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold truncate">{message.subject}</div>
            <div className="text-xs text-white/50 truncate">From {message.from_name || ""} &lt;{message.from_email}&gt; · {fmtDateTime(message.received_at)}</div>
          </div>
          <button onClick={() => onClose()} className="text-white/50 hover:text-white text-xl leading-none">✕</button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {message.status !== "new" && (
            <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/60 flex items-center justify-between gap-3">
              <span>
                This message is marked <strong className="text-white">{message.status}</strong>
                {message.replied_at ? ` · replied ${fmtDateTime(message.replied_at)}` : ""}
              </span>
              <button
                onClick={() => setStatus("new")}
                data-testid="lumi-reopen-btn"
                className="text-xs px-2 py-1 border border-white/20 rounded-md hover:bg-white/10"
              >
                ↩ Re-open
              </button>
            </div>
          )}

          {isForwarded && (
            <div className="bg-orange-500/10 border border-orange-400/30 rounded-lg p-3 text-sm" data-testid="lumi-forwarded-warning">
              <div className="text-orange-300 font-medium mb-1">⚠️ This looks forwarded</div>
              <div className="text-orange-100/80 text-xs leading-relaxed">
                The "From" address on this email is <strong>{message.from_email}</strong>, which may be the forwarder (you) rather than the original customer.
                Replying will go to that address. Edit the <strong>Reply to</strong> field below to send to the real customer instead.
              </div>
            </div>
          )}

          {message.summary && (
            <div className="bg-amber-200/10 border border-amber-200/20 rounded-lg p-3 text-sm">
              <div className="text-xs text-amber-200/70 uppercase tracking-wider mb-1">Lumi's summary</div>
              <div className="text-amber-100">{INTENT_LABEL[message.intent] || ""} · {message.summary}</div>
            </div>
          )}

          {/* Conversation history — collapsed by default; only shown when there's more than just the current message */}
          {thread && thread.turns && thread.turns.length > 1 && (
            <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden" data-testid="lumi-thread-history">
              <button
                onClick={() => setShowHistory((v) => !v)}
                data-testid="lumi-thread-toggle"
                className="w-full px-3 py-2 text-left text-xs flex items-center justify-between hover:bg-white/5"
              >
                <span className="text-white/70">
                  📜 Conversation history — {thread.turns.length} messages in this thread
                </span>
                <span className="text-white/40">{showHistory ? "▾ hide" : "▸ show"}</span>
              </button>
              {showHistory && (
                <div className="border-t border-white/10 max-h-72 overflow-y-auto p-3 space-y-3">
                  {thread.turns.map((t, i) => (
                    <div
                      key={`${t.kind}-${t.id || i}-${t.at || i}`}
                      className={`text-xs rounded-md p-2 ${
                        t.kind === "outbound"
                          ? "bg-emerald-200/5 border border-emerald-300/20"
                          : t.is_current
                          ? "bg-amber-200/10 border border-amber-200/30"
                          : "bg-white/5 border border-white/10"
                      }`}
                      data-testid={`lumi-thread-turn-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="font-medium truncate">
                          {t.kind === "outbound" ? (
                            <span className="text-emerald-300">💌 You replied</span>
                          ) : (
                            <span className={t.is_current ? "text-amber-200" : "text-white/70"}>
                              {t.is_current ? "← This message" : "📩"} {t.from_name || t.from_email}
                            </span>
                          )}
                        </div>
                        <div className="text-white/40 shrink-0">
                          {t.at ? fmtDateTimeShort(t.at) : ""}
                        </div>
                      </div>
                      <pre className="whitespace-pre-wrap break-words font-sans text-white/70 max-h-32 overflow-y-auto">{(t.body_text || "").slice(0, 1500)}{(t.body_text || "").length > 1500 ? "…" : ""}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {loadingThread && !thread && (
            <div className="text-xs text-white/30">Loading thread history…</div>
          )}

          <div>
            <div className="text-xs text-white/50 mb-1">Original message</div>
            <pre className="bg-white/5 border border-white/10 rounded-lg p-3 text-xs whitespace-pre-wrap break-words font-sans max-h-60 overflow-y-auto">{message.body_text || "(empty)"}</pre>
          </div>

          <div>
            <label className="text-xs text-white/50 mb-1 block">Reply to</label>
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              data-testid="lumi-reply-to"
              placeholder="customer@example.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
            />
            {toEmail && toEmail.toLowerCase() !== (message.from_email || "").toLowerCase() && (
              <div className="text-xs text-sky-300/80 mt-1">
                ✏️ Overridden — original was {message.from_email}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <div className="text-xs text-white/50">Your reply (Lumi drafts a starting point)</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowSnippets(true); setSnippetTab("text"); setSnippetQuery(""); }}
                  data-testid="lumi-snippets-toggle"
                  className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10"
                  title={snippets.length === 0 ? "No snippets yet — create some in the Snippets tab" : "Insert a saved snippet"}
                >
                  📋 Snippets {snippets.length > 0 ? `(${snippets.length})` : ""}
                </button>
                <button onClick={analyse} disabled={analysing} data-testid="lumi-analyse-btn" className="text-xs border border-white/20 rounded-full px-3 py-1 hover:bg-white/10 disabled:opacity-50">
                  {analysing ? "Analysing…" : message.ai_reply_draft ? "↻ Regenerate" : "✨ Generate draft"}
                </button>
              </div>
            </div>
            <textarea
              ref={replyRef}
              data-testid="lumi-reply-body"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={10}
              placeholder="Write or click 'Generate draft' to let Lumi suggest one…"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:border-white/30"
            />
          </div>

          {/* Chat-refine panel */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid="lumi-refine-panel">
            <div className="text-xs text-white/50 mb-2 flex items-center gap-2">
              <span>💬 Chat with Lumi to tweak the draft</span>
              <span className="text-white/30">— e.g. "make it warmer" · "mention picture cards" · "shorter"</span>
            </div>
            {refineMessages.length > 0 && (
              <div className="space-y-2 max-h-44 overflow-y-auto pr-1 mb-2">
                {refineMessages.map((m, i) => (
                  <div key={i} className={`text-xs ${m.role === "you" ? "text-right" : "text-left"}`}>
                    <div
                      className={`inline-block px-2.5 py-1.5 rounded-lg max-w-[85%] whitespace-pre-wrap ${
                        m.role === "you"
                          ? "bg-white/10 text-white/90"
                          : "bg-amber-200/10 border border-amber-200/20 text-amber-100"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {refining && (
                  <div className="text-xs text-amber-100/60">Lumi is thinking…</div>
                )}
                <div ref={refineEndRef} />
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={refineInput}
                onChange={(e) => setRefineInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); refine(); } }}
                placeholder='Tell Lumi how to tweak the draft…'
                disabled={refining}
                data-testid="lumi-refine-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 disabled:opacity-50"
              />
              <button
                onClick={refine}
                disabled={refining || !refineInput.trim()}
                data-testid="lumi-refine-send"
                className="text-sm px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg disabled:opacity-50"
              >
                {refining ? "…" : "Tweak"}
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 p-4 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-3 text-xs text-white/50">
            {message.status !== "archived" && (
              <button
                onClick={() => setStatus("archived")}
                data-testid="lumi-archive-btn"
                className="hover:text-white underline underline-offset-2"
              >
                Archive
              </button>
            )}
            {message.status === "new" && (
              <button
                onClick={() => setStatus("handled")}
                data-testid="lumi-handled-btn"
                className="hover:text-white underline underline-offset-2"
                title="Only use if you replied outside Lumi — sending a reply here marks it handled automatically"
              >
                Mark handled (no reply)
              </button>
            )}
          </div>
          <button data-testid="lumi-send-reply" onClick={send} disabled={sending || !reply.trim()} className="text-sm px-4 py-2 bg-white text-black rounded-lg font-medium hover:bg-white/90 disabled:opacity-50">
            {sending ? "Sending…" : "💌 Send reply"}
          </button>
        </div>
      </div>

      {/* Snippet picker — Text/Video tabs + search, mirrors the Compose chat picker */}
      {showSnippets && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowSnippets(false); setSnippetQuery(""); } }}
        >
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col" data-testid="lumi-inbox-snippets-modal">
            <div className="px-5 py-3 border-b border-white/10">
              <div className="font-bold">Insert snippet into reply</div>
              <div className="text-xs text-white/50 mt-1">Click a snippet to insert it at your cursor.</div>
              <div className="flex gap-1.5 mt-3">
                <button
                  onClick={() => setSnippetTab("text")}
                  data-testid="lumi-inbox-snippet-tab-text"
                  className={`text-xs px-3 py-1 rounded-full border transition-all ${snippetTab === "text" ? "bg-white text-black border-white" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}
                >
                  📋 Text
                </button>
                <button
                  onClick={() => setSnippetTab("video")}
                  data-testid="lumi-inbox-snippet-tab-video"
                  className={`text-xs px-3 py-1 rounded-full border transition-all ${snippetTab === "video" ? "bg-rose-300/30 text-rose-50 border-rose-300/60" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}
                >
                  📺 Video
                </button>
                <input
                  type="search"
                  value={snippetQuery}
                  onChange={(e) => setSnippetQuery(e.target.value)}
                  placeholder="🔍 Search snippets…"
                  data-testid="lumi-inbox-snippet-search"
                  autoFocus
                  className="flex-1 ml-1 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs focus:outline-none focus:border-white/30"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-white/5">
              {(() => {
                const q = snippetQuery.trim().toLowerCase();
                let visible = snippets.filter((s) => (s.kind || "text") === snippetTab);
                if (q) {
                  visible = visible.filter((s) =>
                    (s.label || "").toLowerCase().includes(q) ||
                    (s.body || "").toLowerCase().includes(q)
                  );
                  visible.sort((a, b) => {
                    const al = (a.label || "").toLowerCase();
                    const bl = (b.label || "").toLowerCase();
                    const ap = al.startsWith(q) ? 0 : 1;
                    const bp = bl.startsWith(q) ? 0 : 1;
                    return ap !== bp ? ap - bp : al.localeCompare(bl);
                  });
                }
                if (visible.length === 0) {
                  return (
                    <div className="p-6 text-center text-xs text-white/40">
                      {q
                        ? <>No {snippetTab} snippets match <strong className="text-white/70">"{snippetQuery}"</strong>.</>
                        : <>No {snippetTab} snippets yet — create some in the <strong className="text-white/60">Snippets</strong> tab.</>
                      }
                    </div>
                  );
                }
                return visible.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => insertSnippet(s)}
                    data-testid={`lumi-snippet-insert-${s.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-white/5"
                  >
                    <div className="flex items-center gap-2">
                      {s.kind === "video" && <span className="text-[10px] bg-rose-300/20 border border-rose-300/40 text-rose-100 rounded px-1.5 py-0.5 shrink-0">📺 video</span>}
                      <div className="text-sm font-medium truncate">{s.label}</div>
                    </div>
                    <div className="text-xs text-white/50 line-clamp-2 whitespace-pre-wrap mt-0.5">{s.body}</div>
                  </button>
                ));
              })()}
            </div>
            <div className="px-5 py-3 border-t border-white/10 text-right">
              <button onClick={() => { setShowSnippets(false); setSnippetQuery(""); }} className="text-xs text-white/60 hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
